// App-side delegate for `@heidi/session/data` (ARCFE-994). The SERVER half
// moved — ordered delete, version-dispatched optimistic surgery + rollback,
// session-cache invalidations, the domain-event emit. This file owns the
// OFFLINE half (six capture atoms, the Tauri bridge, tombstones — injected
// as callables at their legacy positions per the cohort-C rule) and every
// app reaction (toasts, analytics, integrations invalidations), all
// byte-identical.
import { stopSessionGeneration } from '@/features/note-generation/generation-coordinator';
import { clearSideBySideSessionState } from '@/features/sideBySideNoteGen/atoms/atoms';
import { toast } from '@heidi/design-system/toast';

// LOAD-BEARING side-effect imports — the legacy module's dropped value
// imports (the delegate-import-graph rule from D5/ARCFE-990: the v1 hub's
// graph value-imports `@/store` via scribeHTTP). Deleted with this delegate
// at Z2.
import '@/atoms/sessionAtoms';
import '@/hooks/query/useQueryCombinedSessions';
import '@/hooks/query/useQuerySessionById';
import '@/pages/api/scribeAPI';
import '../query/useQuerySessions';
import '../query/useQuerySessionsV2';
import '../query/useQuerySessionsWithPagination';

import {
  offlineActiveRecordingIdAtom,
  offlineCapturePausedAtom,
  offlineDeletedLocalSessionIdsAtom,
  offlineLocalSessionsAtom,
  offlineNoteFailedDismissedSessionIdsAtom,
  offlineNoteFailedSessionIdsAtom,
  offlineSyncQueueAtom,
  type OfflineLocalSessionListItem,
} from '@/atoms/offline-recording';
import { useIsOfflineConnectivity } from '@/hooks/desktop/useIsOfflineConnectivity';
import { invalidateIntegrationsMixinGetScheduleQuery } from '@/hooks/integrations/integrations-framework/mixins/useQueryIntegrationsMixinGetSchedule';
import {
  IntegrationLaunchSource,
  useIntegrationEhrInfo,
} from '@/hooks/integrations/useIntegrationEhrInfo';
import { sessionDeleteByIds } from '@/lib/analytics/scribe/session';
import { offlineRecordingBridge } from '@/tauri/offline-recording/recording';
import { isTauri } from '@/tauri/utils';
import type { OfflineSyncItem } from '@/types/offline-recording';
import {
  trackOfflineSessionDeleteEnqueueFailed,
  trackOfflineSessionDeleteQueued,
} from '@/utils/analytics/track-offline-recording-events';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@/utils/logger/sanitizeError';
import { isOfflineLocalSession } from '@/utils/offline-recording/localSessionSettings';
import { useTranslations } from '@heidi/runtime/i18n';
import { type QuerySessionResponse } from '@heidi/session/contracts';
import {
  invalidateQueryLinkedSessions,
  useMutateDeleteSessionsByIds as useMutateDeleteSessionsByIdsCore,
  type DeleteSessionsTargets,
} from '@heidi/session/data';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { GET_EHR_APPOINTMENTS } from '../query/integrations/useQueryEHRAppointments';

type OfflineRollback = {
  removedSyncItems: OfflineSyncItem[];
  removedLocalSessions: OfflineLocalSessionListItem[];
  removedNoteFailedSessionIds: string[];
  removedNoteFailedDismissedSessionIds: string[];
  tombstonedIds: string[];
};

const unique = (values: string[]) => [...new Set(values)];

const LIVE_OFFLINE_CAPTURE_DELETE = 'LiveOfflineCaptureDeleteError';

class LiveOfflineCaptureDeleteError extends Error {
  constructor() {
    super(LIVE_OFFLINE_CAPTURE_DELETE);
    this.name = LIVE_OFFLINE_CAPTURE_DELETE;
  }
}

const isLiveOfflineCaptureDeleteError = (error: unknown): boolean =>
  error instanceof LiveOfflineCaptureDeleteError ||
  (error instanceof Error && error.name === LIVE_OFFLINE_CAPTURE_DELETE);

const isLiveCaptureOnDeleteTargets = (
  activeRecordingId: string | null,
  targets: DeleteSessionsTargets,
  queue: OfflineSyncItem[],
  localSessions: OfflineLocalSessionListItem[]
): boolean => {
  if (!activeRecordingId) return false;
  if (
    targets.offlineLocalSessionIds.includes(activeRecordingId) ||
    targets.serverSessionIds.includes(activeRecordingId) ||
    targets.cacheSessionIds.includes(activeRecordingId)
  ) {
    return true;
  }
  // Queue first, then the local-session list: a first recording on an
  // existing server session has no queue row until it stops (the queue only
  // hydrates from `pending_sync` / `offline_recording_list` on stop), so the
  // queue alone misses every live capture between create() and the first
  // stop. `offlineLocalSessionsAtom` is written at create() with the
  // server id already attached, so it is the only place that mapping lives
  // until the queue catches up. Same precedence `useRecordingButton` uses to
  // resolve a live capture's identity.
  const liveRowServerSessionId =
    queue.find((item) => item.localSessionId === activeRecordingId)?.serverSessionId ??
    localSessions.find((session) => session.localSessionId === activeRecordingId)
      ?.serverSessionId ??
    null;
  return (
    liveRowServerSessionId != null &&
    (targets.serverSessionIds.includes(liveRowServerSessionId) ||
      targets.cacheSessionIds.includes(liveRowServerSessionId))
  );
};

const appendUnique = (prev: string[], values: string[]): string[] => {
  const next = new Set(prev);
  values.forEach((value) => next.add(value));
  return [...next];
};

/**
 * EVERY queue row the delete reaches, not the first one it matches.
 *
 * `serverSessionId` is not unique across the queue: `useRecordingButton` stamps
 * it onto a fresh capture shell each time an offline capture starts from an
 * already-open session, so one encounter can hold several local rows pointing at
 * one server session. A first-match delete removed one of them and left the
 * sibling's audio on disk, queued to upload into a session it had just deleted.
 *
 * Matching is transitive because deletion is: a row resolved by its
 * `localSessionId` still sends its `serverSessionId` to the backend, so every
 * other row pointing at that same server session is about to be orphaned too and
 * has to come with it. Following server ids only from rows already being
 * deleted keeps the set closed over the encounter — it never reaches a session
 * the caller did not ask to delete.
 *
 * Deliberately NOT the shared `findOfflineSyncQueueItem`, which skips capture
 * shells matched by server id — right for reading a server session's sync
 * state, wrong here, where a shell is still a row a delete has to remove.
 */
const findOfflineQueueItems = (
  queueByAnyId: Map<string, OfflineSyncItem[]>,
  sessionId: string
): OfflineSyncItem[] => {
  const matched = new Map<string, OfflineSyncItem>();
  const pendingIds = [sessionId];
  const visitedIds = new Set<string>();

  while (pendingIds.length > 0) {
    const id = pendingIds.pop() as string;
    if (visitedIds.has(id)) continue;
    visitedIds.add(id);

    for (const item of queueByAnyId.get(id) ?? []) {
      if (matched.has(item.localSessionId)) continue;
      matched.set(item.localSessionId, item);
      if (item.serverSessionId) pendingIds.push(item.serverSessionId);
    }
  }

  return [...matched.values()];
};

/**
 * Both id spaces in one map, built once per delete rather than once per id the
 * closure reaches, so the walk above stays linear in the queue however many
 * rows one encounter turns out to hold.
 */
const indexOfflineQueueByAnyId = (
  queue: OfflineSyncItem[]
): Map<string, OfflineSyncItem[]> => {
  const byId = new Map<string, OfflineSyncItem[]>();

  const add = (id: string | null | undefined, item: OfflineSyncItem) => {
    if (!id) return;
    const bucket = byId.get(id);
    if (bucket) bucket.push(item);
    else byId.set(id, [item]);
  };

  for (const item of queue) {
    add(item.localSessionId, item);
    add(item.serverSessionId, item);
  }

  return byId;
};

const isOfflineRecordNotFoundError = (error: unknown): boolean => {
  if (typeof error === 'string') return error.includes('Recording not found');
  if (typeof error !== 'object' || error === null) return false;
  const { message, error_code: errorCode } = error as {
    message?: unknown;
    error_code?: unknown;
  };
  return (
    errorCode === 'OFFLINE_RECORD_NOT_FOUND' ||
    (typeof message === 'string' && message.includes('Recording not found'))
  );
};

async function deleteOfflineRecordingIfPresent(localSessionId: string): Promise<void> {
  try {
    await offlineRecordingBridge.delete(localSessionId);
  } catch (error) {
    if (isOfflineRecordNotFoundError(error)) return;
    throw error;
  }
}

function resolveDeleteTargets({
  sessionIds,
  queue,
  localSessionIds,
  getCachedSession,
}: {
  sessionIds: string[];
  queue: OfflineSyncItem[];
  localSessionIds: Set<string>;
  getCachedSession: (sessionId: string) => QuerySessionResponse | undefined;
}): DeleteSessionsTargets {
  const serverSessionIds: string[] = [];
  const offlineLocalSessionIds: string[] = [];
  const cacheSessionIds = [...sessionIds];
  const queueByAnyId = indexOfflineQueueByAnyId(queue);

  for (const sessionId of sessionIds) {
    const queueItems = findOfflineQueueItems(queueByAnyId, sessionId);
    const cachedSession = getCachedSession(sessionId);
    const isLocalOnlySession =
      localSessionIds.has(sessionId) || isOfflineLocalSession(cachedSession);

    if (queueItems.length > 0) {
      // A row with no `serverSessionId` has no server side to delete: the index
      // holds only the two id spaces, so such a row can only have been reached
      // by its own `localSessionId`, and it is disk-only by definition.
      for (const queueItem of queueItems) {
        offlineLocalSessionIds.push(queueItem.localSessionId);
        if (queueItem.serverSessionId) {
          serverSessionIds.push(queueItem.serverSessionId);
          cacheSessionIds.push(queueItem.serverSessionId);
        }
      }
      continue;
    }

    if (isLocalOnlySession) {
      offlineLocalSessionIds.push(sessionId);
      continue;
    }

    serverSessionIds.push(sessionId);
  }

  return {
    serverSessionIds: unique(serverSessionIds),
    offlineLocalSessionIds: unique(offlineLocalSessionIds),
    cacheSessionIds: unique(cacheSessionIds),
  };
}

export function useMutateDeleteSessionsByIds() {
  const {
    integrationEhrProvider,
    integrationLaunchSource,
    isIntegrationOnboardingSession,
  } = useIntegrationEhrInfo();
  const { t } = useTranslations();
  const intl = useIntl();
  const queryClient = useQueryClient();
  // THE offline predicate — the same one every other server-touching surface
  // reads, not a second one minted here. It already folds in `isTauri()` and
  // `useIsOfflineRecordingEnabled()`, so the umbrella flag being off makes it
  // false and no delete is ever deferred. Web and online both fall through to
  // the request unchanged.
  //
  // The thunk below closes over this render's value, and the port invokes it
  // at mutation time, so a delete reads whatever the last committed render
  // saw. That covers a drop React has already processed — this hook
  // re-renders on one, since the predicate subscribes to the connectivity atom
  // and to `navigator.onLine`. What it does not cover is a drop landing
  // between that commit and the click; that delete goes to the backend and
  // fails into the existing error path rather than queueing.
  //
  // A ref would not change this: assigned during render it holds the same
  // value as the closure, and assigned in an effect it lags by one commit. A
  // genuinely call-time read means re-deriving the predicate from the atom,
  // the flag, `navigator.onLine` and the env toggle at the call site — a
  // second copy of `useIsOfflineConnectivity` that can drift from the one
  // every other surface reads. Not worth it for this window.
  const isOfflineConnectivity = useIsOfflineConnectivity();
  const refusedLiveCaptureRef = useRef(false);

  const offlineSyncQueue = useAtomValue(offlineSyncQueueAtom);
  const offlineLocalSessions = useAtomValue(offlineLocalSessionsAtom);
  const offlineActiveRecordingId = useAtomValue(offlineActiveRecordingIdAtom);
  const offlineCapturePaused = useAtomValue(offlineCapturePausedAtom);
  const offlineNoteFailedSessionIds = useAtomValue(offlineNoteFailedSessionIdsAtom);
  const offlineNoteFailedDismissedSessionIds = useAtomValue(
    offlineNoteFailedDismissedSessionIdsAtom
  );
  const setOfflineSyncQueue = useSetAtom(offlineSyncQueueAtom);
  const setOfflineLocalSessions = useSetAtom(offlineLocalSessionsAtom);
  const setOfflineActiveRecordingId = useSetAtom(offlineActiveRecordingIdAtom);
  const setOfflineDeletedLocalSessionIds = useSetAtom(offlineDeletedLocalSessionIdsAtom);
  const setOfflineNoteFailedSessionIds = useSetAtom(offlineNoteFailedSessionIdsAtom);
  const setOfflineNoteFailedDismissedSessionIds = useSetAtom(
    offlineNoteFailedDismissedSessionIdsAtom
  );

  return useMutateDeleteSessionsByIdsCore({
    // See the package hook's prop doc — desktop must run the local delete
    // while offline; web keeps the default queue-until-reconnect behaviour.
    networkMode: isTauri() ? 'offlineFirst' : 'online',
    resolveTargets: (sessionIds, getCachedSession) =>
      resolveDeleteTargets({
        sessionIds,
        queue: offlineSyncQueue,
        localSessionIds: new Set(
          offlineLocalSessions.map((session) => session.localSessionId)
        ),
        getCachedSession,
      }),
    shouldDeferServerDelete: () => isOfflineConnectivity,
    /**
     * Hand the delete to the durable Rust queue instead of the backend.
     *
     * Rethrows on purpose. The clinician's row is already hidden, and this
     * rejection is the only thing that brings it back — a swallowed failure
     * would leave them believing a session was deleted that still exists
     * everywhere. Browser storage could not offer that: it would have accepted
     * the write, returned success, and lost the delete silently.
     */
    enqueueServerDeletes: async (serverSessionIds) => {
      try {
        await offlineRecordingBridge.enqueueServerDeletes(serverSessionIds);
      } catch (error) {
        // No `error_code`: this rejection is a sanitized Display STRING, not a
        // coded object. The `OFFLINE_*` code travels on event payloads, and
        // none of the Display strings embeds it, so there is nothing to read
        // or parse here. `error` itself is never forwarded — a database
        // failure carries the statement text.
        trackOfflineSessionDeleteEnqueueFailed({
          session_count: serverSessionIds.length,
          source: 'offline_recording',
        });
        throw error;
      }
      trackOfflineSessionDeleteQueued({
        session_count: serverSessionIds.length,
        source: 'offline_recording',
      });
    },
    deleteLocalRecordings: async (localSessionIds) => {
      await Promise.all(
        localSessionIds.map((localSessionId) =>
          deleteOfflineRecordingIfPresent(localSessionId)
        )
      );
    },
    applyOfflineOptimistic: (targets): OfflineRollback | undefined => {
      if (
        isLiveCaptureOnDeleteTargets(
          offlineActiveRecordingId,
          targets,
          offlineSyncQueue,
          offlineLocalSessions
        )
      ) {
        toast({
          tone: 'destructive',
          title: offlineCapturePaused
            ? intl.formatMessage({
                defaultMessage: 'Session currently paused',
                id: 'yMbn2u17Zu',
                description:
                  'Toast title shown when a user tries to delete a session that is currently paused',
              })
            : intl.formatMessage({
                defaultMessage: 'Session currently in progress',
                id: '0A15XmQmgR',
                description:
                  'Toast title shown when a user tries to delete a session that is currently in progress (still recording)',
              }),
          description: intl.formatMessage({
            defaultMessage: 'Please end the session before deleting.',
            id: 'Y1ilCwmpk9',
            description:
              'Toast description shown when a user tries to delete a session that is still recording or paused',
          }),
        });
        // Thrown, not returned: this is the first statement the core runs in
        // `onMutate`, ahead of every cache write, so a throw here is the only
        // seam the app half has that refuses the delete with nothing yet
        // hidden and `mutationFn` never entered. `onError` then sees a
        // rollback token of `undefined` — correct, since nothing was applied.
        throw new LiveOfflineCaptureDeleteError();
      }

      // Note-failure state outlives the sync-queue row that carries the
      // local↔server mapping, so once a recording has synced it is keyed by the
      // *server* id (re-keyed at `offline_recording:synced`). A delete after
      // that point resolves no local id at all, so clearing on
      // `offlineLocalSessionIds` alone would leave an undismissed warning for a
      // deleted session and a dismissal persisted forever. Clear on both id
      // spaces. (The same gap predates this change for the active-failure list;
      // fixing it here covers both lists rather than only the new one.)
      const noteFailedTargetIds = unique([
        ...targets.offlineLocalSessionIds,
        ...targets.serverSessionIds,
      ]);

      // Snapshot the offline items removed optimistically so onError can restore
      // them (mirrors the individual session-card / context-menu delete handlers).
      const offlineRollback: OfflineRollback | undefined =
        targets.offlineLocalSessionIds.length > 0 || noteFailedTargetIds.length > 0
          ? {
              removedSyncItems: offlineSyncQueue.filter((item) =>
                targets.offlineLocalSessionIds.includes(item.localSessionId)
              ),
              removedLocalSessions: offlineLocalSessions.filter((item) =>
                targets.offlineLocalSessionIds.includes(item.localSessionId)
              ),
              removedNoteFailedSessionIds: offlineNoteFailedSessionIds.filter((id) =>
                noteFailedTargetIds.includes(id)
              ),
              removedNoteFailedDismissedSessionIds:
                offlineNoteFailedDismissedSessionIds.filter((id) =>
                  noteFailedTargetIds.includes(id)
                ),
              tombstonedIds: targets.offlineLocalSessionIds,
            }
          : undefined;

      if (targets.offlineLocalSessionIds.length > 0) {
        setOfflineSyncQueue((prev) =>
          prev.filter(
            (item) => !targets.offlineLocalSessionIds.includes(item.localSessionId)
          )
        );
        setOfflineLocalSessions((prev) =>
          prev.filter(
            (item) => !targets.offlineLocalSessionIds.includes(item.localSessionId)
          )
        );
        setOfflineActiveRecordingId((prev) =>
          prev !== null && targets.offlineLocalSessionIds.includes(prev) ? null : prev
        );
        setOfflineDeletedLocalSessionIds((prev) =>
          appendUnique(prev, targets.offlineLocalSessionIds)
        );
      }

      // Outside the local-id guard above: a post-sync delete carries only a
      // server id, so `offlineLocalSessionIds` is empty and that branch never
      // runs — which is exactly the case this cleanup has to cover.
      if (noteFailedTargetIds.length > 0) {
        setOfflineNoteFailedSessionIds((prev) =>
          prev.filter((id) => !noteFailedTargetIds.includes(id))
        );
        setOfflineNoteFailedDismissedSessionIds((prev) =>
          prev.filter((id) => !noteFailedTargetIds.includes(id))
        );
      }

      return offlineRollback;
    },
    rollbackOffline: (token) => {
      const offlineRollback = token as OfflineRollback;
      const {
        removedSyncItems,
        removedLocalSessions,
        removedNoteFailedSessionIds,
        removedNoteFailedDismissedSessionIds,
        tombstonedIds,
      } = offlineRollback;
      if (removedSyncItems.length > 0) {
        setOfflineSyncQueue((prev) => {
          const existing = new Set(prev.map((item) => item.localSessionId));
          const restored = removedSyncItems.filter(
            (item) => !existing.has(item.localSessionId)
          );
          return restored.length > 0 ? [...restored, ...prev] : prev;
        });
      }
      if (removedLocalSessions.length > 0) {
        setOfflineLocalSessions((prev) => {
          const existing = new Set(prev.map((item) => item.localSessionId));
          const restored = removedLocalSessions.filter(
            (item) => !existing.has(item.localSessionId)
          );
          return restored.length > 0 ? [...restored, ...prev] : prev;
        });
      }
      setOfflineDeletedLocalSessionIds((prev) =>
        prev.filter((id) => !tombstonedIds.includes(id))
      );
      if (removedNoteFailedSessionIds.length > 0) {
        setOfflineNoteFailedSessionIds((prev) =>
          appendUnique(prev, removedNoteFailedSessionIds)
        );
      }
      if (removedNoteFailedDismissedSessionIds.length > 0) {
        setOfflineNoteFailedDismissedSessionIds((prev) =>
          appendUnique(prev, removedNoteFailedDismissedSessionIds)
        );
      }
    },
    onSessionCleanup: (sessionId) => {
      stopSessionGeneration(sessionId, 'cleanup');
      clearSideBySideSessionState(sessionId);
    },
    onDeleted: (input) => {
      // if user has ehr profile, invalidate ehr appointments
      if (integrationEhrProvider) {
        if (isIntegrationOnboardingSession) {
          void invalidateIntegrationsMixinGetScheduleQuery();
        } else if (
          integrationLaunchSource !== IntegrationLaunchSource.GENERIC_EHR_LAUNCH
        ) {
          void queryClient.invalidateQueries({
            queryKey: [GET_EHR_APPOINTMENTS],
          });
        }
      }
      void invalidateQueryLinkedSessions();

      toast({
        tone: 'success',
        title:
          input.sessionIds.length > 1
            ? t('session.sessionsDeleted', { count: input.sessionIds.length })
            : t('session.sessionDeleted'),
      });
      // tracking
      sessionDeleteByIds({
        sessionCount: input.sessionIds.length,
      });
    },
    onDeleteError: (error) => {
      // The core calls this and `onDeleteFailed` back to back inside one
      // `onError`, but only this one is handed the error — hence the ref. Its
      // whole lifetime is those two statements; it is not mutation state.
      if (isLiveOfflineCaptureDeleteError(error)) {
        refusedLiveCaptureRef.current = true;
        // A refusal is not a failure: nothing was attempted, so there is no
        // sanitised error worth forwarding to Datadog.
        return;
      }
      // sanitizeError drops PHI: the axios error for /sessions/batch carries the
      // session ids and response body, and error logs are forwarded to Datadog.
      logger.error('Failed to delete sessions', sanitizeError(error));
    },
    onDeleteFailed: ({ sessionIds }) => {
      if (refusedLiveCaptureRef.current) {
        refusedLiveCaptureRef.current = false;
        return;
      }
      toast({
        tone: 'destructive',
        title: intl.formatMessage(
          {
            defaultMessage:
              '{count, plural, one {Failed to delete session} other {Failed to delete sessions}}',
            id: '8mKcNeqwdY',
          },
          { count: sessionIds.length }
        ),
        description: intl.formatMessage({
          defaultMessage: 'Please try again.',
          id: 'VjB59sDdPt',
        }),
      });
    },
  });
}
